import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, Clock, Tag, ChevronLeft, ChevronRight, Search, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import AnimatedLogo from "../components/AnimatedLogo";
import { API } from "../App";
import { optimizedImageUrl } from "../lib/imageUrl";

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  
  const page = parseInt(searchParams.get("page") || "1");
  const category = searchParams.get("category");
  const tag = searchParams.get("tag");
  const search = searchParams.get("search");

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [page, category, tag, search]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      let url = `${API}/blog/posts?page=${page}&limit=9`;
      if (category) url += `&category=${category}`;
      if (tag) url += `&tag=${tag}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/blog/categories`);
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery.trim() });
    } else {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchQuery("");
  };

  return (
    <>
      <Helmet>
        <title>Blog | Happy Ages - Custom Coat of Arms & Celebration Designs</title>
        <meta name="description" content="Discover insights about family heritage, coat of arms history, heraldry traditions, and custom celebration designs." />
        <meta property="og:title" content="Happy Ages Blog" />
        <meta property="og:description" content="Insights about family heritage, coat of arms, and celebration traditions." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
      </Helmet>

      <div className="min-h-screen bg-[#020617]">
        <Header />

        {/* Hero */}
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-cinzel text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
              {category ? `${category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}` : 
               tag ? `Posts tagged "${tag}"` :
               search ? `Search: "${search}"` :
               <>HAPPY AGES <span className="text-[#D4AF37]">BLOG</span></>}
            </h1>
            <p className="font-manrope text-[#94A3B8] text-lg max-w-2xl mx-auto">
              Explore the rich history of heraldry, family traditions, and tips for creating meaningful celebrations.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 pb-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Posts Grid */}
            <div className="flex-1">
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="mb-8">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <Input
                      type="text"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-[#1E293B]/50 border-[#334155] text-[#F8FAFC] placeholder:text-[#94A3B8]"
                    />
                  </div>
                  <Button type="submit" className="bg-[#D4AF37] text-[#020617] hover:bg-[#B5952F]">
                    Search
                  </Button>
                </div>
              </form>

              {(category || tag || search) && (
                <div className="mb-6 flex items-center gap-2">
                  <span className="text-[#94A3B8]">Filtering by:</span>
                  <span className="bg-[#D4AF37]/20 text-[#D4AF37] px-3 py-1 rounded-full text-sm">
                    {category || tag || search}
                  </span>
                  <button onClick={clearFilters} className="text-[#94A3B8] hover:text-[#F8FAFC] underline text-sm">
                    Clear
                  </button>
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="animate-pulse">
                      <div className="bg-[#1E293B] h-48 rounded-xl mb-4"></div>
                      <div className="bg-[#1E293B] h-6 rounded mb-2"></div>
                      <div className="bg-[#1E293B] h-4 rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#94A3B8] text-lg">No posts found.</p>
                  {(category || tag || search) && (
                    <button onClick={clearFilters} className="mt-4 text-[#D4AF37] hover:underline">
                      View all posts
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map(post => (
                    <article key={post.id} className="group card-royal p-4">
                      <Link to={`/blog/${post.slug}`}>
                        {post.featured_image ? (
                          <img
                            src={optimizedImageUrl(post.featured_image, 800)}
                            alt={post.featured_image_alt || post.title}
                            width="400"
                            height="192"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-48 object-cover rounded-lg mb-4 group-hover:opacity-90 transition-opacity"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-lg mb-4 flex items-center justify-center border border-[#D4AF37]/20">
                            <span className="text-4xl">📜</span>
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-sm text-[#94A3B8] mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(post.published_at || post.created_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {post.reading_time} min read
                          </span>
                        </div>
                        <h2 className="font-cinzel text-lg font-bold text-[#F8FAFC] mb-2 group-hover:text-[#D4AF37] transition-colors">
                          {post.title}
                        </h2>
                        <p className="font-manrope text-[#94A3B8] text-sm line-clamp-2">{post.excerpt}</p>
                      </Link>
                      {post.categories?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {post.categories.map(cat => (
                            <Link
                              key={cat}
                              to={`/blog?category=${cat}`}
                              className="text-xs bg-[#1E293B] text-[#94A3B8] px-2 py-1 rounded hover:bg-[#D4AF37]/20 hover:text-[#D4AF37]"
                            >
                              {cat}
                            </Link>
                          ))}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: page - 1 })}
                    className="border-[#334155] text-[#94A3B8]"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <span className="px-4 text-[#94A3B8]">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: page + 1 })}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:w-72">
              {/* Categories */}
              <div className="card-royal p-6 mb-6">
                <h3 className="font-cinzel font-bold text-[#D4AF37] mb-4">Categories</h3>
                <div className="space-y-2">
                  {categories.map(cat => (
                    <Link
                      key={cat.id}
                      to={`/blog?category=${cat.slug}`}
                      className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors font-manrope ${
                        category === cat.slug 
                          ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                          : 'hover:bg-[#1E293B] text-[#94A3B8]'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-sm bg-[#1E293B] px-2 py-0.5 rounded-full">{cat.post_count}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="card-royal p-6">
                <h3 className="font-cinzel font-bold text-[#D4AF37] text-xl mb-2">Create Your Legacy</h3>
                <p className="font-manrope text-[#94A3B8] text-sm mb-4">
                  Design a custom Coat of Arms that represents your family's unique story.
                </p>
                <Link
                  to="/createarms"
                  className="flex items-center gap-2 bg-[#D4AF37] text-[#020617] px-4 py-2 rounded-lg font-cinzel hover:bg-[#B5952F] transition-colors w-full justify-center"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </aside>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-[#D4AF37]/20 py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] font-cinzel font-bold tracking-wider">
              <AnimatedLogo className="w-5 h-5" />
              HAPPY AGES
            </Link>
            <p className="font-manrope text-[#94A3B8] text-sm mt-2">© {new Date().getFullYear()} Happy Ages. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
